import { createT, type Translator } from '@freightapi/i18n'
import { resolveLocale } from '@freightapi/i18n/locale'
import { type LocaleFor, localesFor } from '@freightapi/i18n/namespaces'
import { UiI18nProvider } from '@freightapi/ui'
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

const STORAGE_KEY = 'freightapi.sign.locale'

/**
 * The languages this app speaks. Four, not three: the inspection page (M2-09) shows the official
 * French CMR box labels, so the `sign` catalogue carries `fr.json` next to `pdf` and every screen
 * — not only the inspection page — can be read in French.
 */
export type SignLocale = LocaleFor<'sign'>

/** Source locale first, which is the order the language switch draws them in. */
export const SIGN_LOCALES = localesFor('sign')

export interface SignI18n {
  readonly locale: SignLocale
  readonly t: Translator<'sign'>
  readonly setLocale: (locale: SignLocale) => void
}

const SignI18nContext = createContext<SignI18n | null>(null)

/**
 * Locale negotiation for the browser, in the order ADR 0010 prescribes for a request: `?locale=`
 * first, then what the reader picked before, then the browser's languages. The choice is kept in
 * `localStorage` because a driver who switches to Polish-accented German once should not have to
 * do it again on the next link.
 */
export function initialLocale(): SignLocale {
  if (typeof window === 'undefined') return 'nl'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return resolveLocale({
    query: new URLSearchParams(window.location.search).get('locale'),
    acceptLanguage: [stored, ...navigator.languages].filter(Boolean).join(','),
    supported: SIGN_LOCALES,
  })
}

export function SignI18nProvider({ children }: { readonly children: ReactNode }) {
  const [locale, setLocaleState] = useState<SignLocale>(initialLocale)

  const setLocale = useCallback((next: SignLocale) => {
    setLocaleState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Private mode or a full quota: the choice simply does not survive the session.
    }
  }, [])

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const value = useMemo<SignI18n>(
    () => ({ locale, t: createT('sign', locale), setLocale }),
    [locale, setLocale],
  )

  return (
    <SignI18nContext value={value}>
      <UiI18nProvider locale={locale} translator={value.t}>
        {children}
      </UiI18nProvider>
    </SignI18nContext>
  )
}

export function useSignI18n(): SignI18n {
  const value = use(SignI18nContext)
  if (value === null) throw new Error('useSignI18n must be used inside <SignI18nProvider>')
  return value
}
